import React, { memo, useCallback, useEffect, useRef } from 'react'
import IconButton from '../common/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import classNames from 'classnames';
import { handleHeightChange } from '../../utils';

type Props = {
  prompt: string;
  onPromptChange?: (value: string) => void;
  onSendMessage?: Function;
  onStopMessage?: Function;
  disabled?: boolean;
  loading?: boolean;
}

function ChatInput({ prompt, disabled, onPromptChange, onSendMessage, loading, onStopMessage }: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSendMessage = useCallback((evt: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLTextAreaElement>) => {
    evt.stopPropagation?.();
    evt.preventDefault?.();
    onSendMessage?.(prompt);
  }, [prompt, onSendMessage]);

  const handlePromptChange = useCallback((evt: React.ChangeEvent<HTMLTextAreaElement>) => {
    onPromptChange?.(evt.target.value);
  }, [onPromptChange]);

  const handlePromptStop = useCallback((evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation?.();
    evt.preventDefault?.();
    onStopMessage?.();
  }, [onStopMessage]);

  const handleEnterKey = useCallback((evt: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (evt.key === "Enter" && !evt.shiftKey && !evt.ctrlKey) handleSendMessage(evt);
  }, [handleSendMessage]);

  useEffect(() => handleHeightChange(inputRef.current), [prompt]);

  return (
    <form onSubmit={handleSendMessage}>
      <div className={classNames("chat-input-wrapper", { disabled })}>
        <textarea
          id="chat-input"
          ref={inputRef}
          className="chat-input"
          placeholder="Type message here..."
          value={prompt}
          onChange={handlePromptChange}
          onKeyDown={handleEnterKey}
          autoFocus
          disabled={disabled}
          rows={1}
        />

        <IconButton
          type={loading ? "button" : "submit"}
          disabled={loading ? !onStopMessage : (disabled || !prompt.length)}
          className="chat-input-submit"
          tooltip={{ title: loading ? "Stop Generation" : "Send Message", placement: "left" }}
          onClick={loading ? handlePromptStop : handleSendMessage}
        >
          <FontAwesomeIcon icon={loading ? solid('stop') : solid('arrow-alt-circle-up')} size="lg" />
        </IconButton>
      </div>
    </form>
  )
}

export default memo(ChatInput);